import { motion } from "framer-motion";
import useApiData from "../hooks/useApiData";

const fallbackTestimonialsPage = {
  hero: {
    badge: "Success Stories",
    title: "What Our Athletes Say",
    description:
      "Real stories from real people who've transformed their lives with DVX",
  },
  stats: [],
  items: [],
};

// helper to convert youtube url → embed url
const getEmbedUrl = (url) => {
  try {
    const parsed = new URL(url);

    // youtube.com/watch?v=VIDEO_ID
    if (parsed.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${parsed.searchParams.get("v")}`;
    }

    // youtube.com/shorts/VIDEO_ID
    if (parsed.pathname.includes("/shorts/")) {
      const id = parsed.pathname.split("/shorts/")[1];
      return `https://www.youtube.com/embed/${id}`;
    }

    // youtu.be/VIDEO_ID
    if (parsed.hostname === "youtu.be") {
      return `https://www.youtube.com/embed${parsed.pathname}`;
    }

    return "";
  } catch {
    return "";
  }
};
export default function Testimonials() {
  const { data: pageData, loading } = useApiData(
    "/testimonials-page",
    fallbackTestimonialsPage,
  );

  return (
    <div
      data-testid="testimonials-page"
      className="min-h-screen bg-[var(--dvx-bg)] pt-24 transition-colors duration-300"
    >
      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
              {pageData.hero?.badge}
            </span>
            <h1 className="mt-4 font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              {pageData.hero?.title}
            </h1>
            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              {pageData.hero?.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* VIDEOS */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="font-oswald text-xl text-[var(--dvx-text)] uppercase tracking-widest animate-pulse">
                Loading...
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pageData.testimonials.map((item, index) =>
                item.is_active ? (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] p-4"
                  >
                    {/* Responsive YouTube iframe */}
                    <div className="relative w-full aspect-video mb-4">
                      <iframe
                        src={getEmbedUrl(item.video_url)}
                        title={item.title}
                        className="absolute top-0 left-0 w-full h-full rounded"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>

                    {/* Title */}
                    <h3 className="font-oswald text-lg text-[var(--dvx-text)] uppercase">
                      {item.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[var(--dvx-text-muted)] text-sm mt-2">
                      {item.description}
                    </p>
                  </motion.div>
                ) : null,
              )}
            </div>
          )}
        </div>
      </section>

      {/* STATS (unchanged) */}
      <section className="py-16 bg-[var(--dvx-surface)] transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {pageData.stats?.map((stat, index) => (
              <motion.div
                key={`${stat.label}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-oswald text-4xl md:text-5xl font-bold text-[var(--dvx-primary)]">
                  {stat.value}
                </div>
                <div className="text-[var(--dvx-text-muted)] text-sm uppercase tracking-wider mt-2">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
